import { useRoute, useRouter } from 'vue-router'
// const route = useRoute()
// const router = useRouter()

/**
 * Calculates the progress of the current tab based on a given array of tabs.
 *
 * @param {string} tabs - A comma-separated string of tab names.
 * @return {number} The progress of the current tab as a percentage (0-100).
 */
export const progress = (tabs = '') => {
    tabs = tabs.split(',').map(tab => tab.trim())
    const index = tabs.indexOf(useRoute().query.tab || tabs[0])
    return (index + 1) / tabs.length * 100
}


/**
 * Sets the tab value in the router query and navigates to the new URL.
 *
 * @param {any} tab - The new tab value to be set.
 * @param {object} router - The router object. If not provided, the useRouter() hook will be used.
 * @return {void}
 */
// export const setTab = (tab, router = null) => {
//     if (!router) router = useRouter()
//     console.log('setTab executed from utilities', tab)
//     router.push({ query: { ...router.currentRoute.value.query, tab } })
// }


/**
 * Attaches a click animation to elements with the class 'click-animation'.
 *
 */
export const animateClicks = () => {

    document.addEventListener('click', clickAnimation)

    function clickAnimation(e) {
        const element = e.target
        // Bail, if .click-animation is not present.
        if (!element.classList.contains('click-animation')) return

        let x = event.clientX - element.offsetLeft
        let y = event.clientY - element.offsetTop

        let bubble = document.createElement('div')

        bubble.classList.add('bubble')
        bubble.style.position = 'absolute'
        bubble.style.left = x + 'px'
        bubble.style.top = y + 'px'
        element.appendChild(bubble)

        setTimeout(() => {
            bubble.remove()
        }, 300)
    }
}


/**
 * Calculates the text color based on the background color.
 *
 * @param {string} bgColor - The background color in hex format or rgba format.
 * @return {string} The calculated text color in hex format.
 */
export const textColor = (bgColor = '#333333') => {
    if (bgColor == '' || !bgColor) return '#333333'

    // Convert hex to RGB first
    let r = 0, g = 0, b = 0

    // handling 3 digit hex
    if (bgColor.length == 4) {
        r = "0x" + bgColor[1] + bgColor[1]
        g = "0x" + bgColor[2] + bgColor[2]
        b = "0x" + bgColor[3] + bgColor[3]
    }

    // handling 6 digit hex
    if (bgColor.length == 7) {
        r = "0x" + bgColor[1] + bgColor[2]
        g = "0x" + bgColor[3] + bgColor[4]
        b = "0x" + bgColor[5] + bgColor[6]
    }

    // handling rgba
    if (bgColor.indexOf('rgba') > -1) {
        let rgba = bgColor.replace('rgba(', '').replace(')', '').split(',')
        r = rgba[0]
        g = rgba[1]
        b = rgba[2]
    }

    // Based on luminosity
    let luminosity = (0.299 * r + 0.587 * g + 0.114 * b) / 255

    if (luminosity > 0.4) {
        return '#333333'
    } else {
        return '#FFFFFF'
    }
}


/**
 * Prints the contents of a specified div in a new window and initiates the print process.
 *
 * @param {string} divId - The id of the div containing the contents to be printed.
 * @param {object} options - Optional configuration options for the print feature.
 * @param {string} options.printFeature - The CSS style for the new print window.
 */
export const pagePrint = (divId, options = { printFeature: 'height=600,width=800' }) => {
    if (window && window.print) {
        let printHead = document.head.innerHTML // get the head of the document
        let printContents = document.getElementById(divId).innerHTML // get the contents of the div

        let printWindow = window.open('', 'Print', options.printFeature) // open a new window

        printWindow.document.open()
        printWindow.document.write(printHead + printContents)
        printWindow.document.close()

        setTimeout(() => {
            printWindow.print()
        }, 1000)
    } else {
        console.error("Browser does not support printing")
    }
}

export const separatorMap = {
    'Comma-Dot': { thousand: ',', decimal: '.' },
    'Dot-Comma': { thousand: '.', decimal: ',' },
    'Space-Dot': { thousand: ' ', decimal: '.' },
    'Space-Comma': { thousand: ' ', decimal: ',' }
}

/**
 * getPrice
 * 
 * @param {number} price - the price to be formatted
 * @param {boolean} currency - whether to add currency symbol 
 * @returns {number} formatted price
 */
export const getPrice = (price = 0, currency = true) => {
    const target = typeof prebook_admin !== 'undefined'
        ? prebook_admin.settings.payment
        : prebook.settings.payment

    // return `${target.currency_symbol} ${price}` // remove this return when payment format feature is ready

    const { currency_position, currency_symbol, price_separator, price_decimals } = target || {
        currency_position: 'before',
        currency_symbol: '$',
        price_separator: 'Comma-Dot',
        price_decimals: 2
    }

    let formattedPrice = Number(price).toFixed(price_decimals)

    const separator = separatorMap[price_separator] || separatorMap['Comma-Dot']
    let parts = formattedPrice.split('.')

    // add thousand separators to the integer part
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator.thousand)
    // console.log('parts', parts)
    // Join the parts with the decimal separator
    formattedPrice = parts.join(separator.decimal)

    // apply currency position
    if (!currency) {
        return formattedPrice
    } else if (currency_position === 'before') {
        formattedPrice = currency_symbol + formattedPrice
    } else {
        formattedPrice = formattedPrice + currency_symbol
    }

    return formattedPrice || 0
}

/**
 * Convert a hexadecimal color code to RGBA format.
 *
 * @param {string} colorCode - The hexadecimal or RGB color code.
 * @param {number} [opacity=1] - The opacity of the RGBA color (optional, default is 1).
 * @return {string} The RGBA color code.
 */
export const toRGB = (colorCode, opacity = 1) => {
    // Handle 'transparent' keyword
    if (colorCode === 'transparent') {
        return `rgba(0, 0, 0, 0)` // Return fully transparent rgba
    }

    // validate if colorCode is a valid hex or rgb string
    const isHex = /^#([0-9A-Fa-f]{3}){1,2}$/.test(colorCode)
    const isRGB = /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/.test(colorCode)

    // handle opacity - convert to 0-1 range if it's between 1-100
    if (opacity > 1 && opacity <= 100) {
        opacity = opacity / 100
    } else if (opacity > 100 || opacity < 0) {
        throw new Error(colorCode, 'opacity must be between 0 and 100')
    }

    // handle hex code conversion to rgba
    if (isHex) {
        let hex = colorCode.replace('#', '')

        // convert 3-digit hex to 6-digit
        if (hex.length === 3) {
            hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
        }

        const r = parseInt(hex.substring(0, 2), 16)
        const g = parseInt(hex.substring(2, 4), 16)
        const b = parseInt(hex.substring(4, 6), 16)

        return `rgba(${r},${g},${b},${opacity})`

        // handle rgb to rgba conversion
    } else if (isRGB) {
        // extract the rgb values
        const rgbValues = colorCode.match(/\d+/g).map(Number)

        const [r, g, b] = rgbValues

        // validate if the rgb values are in the range of 0-255
        if ([r, g, b].some(val => val < 0 || val > 255)) {
            throw new Error(colorCode, 'rgb values must be between 0 and 255')
        }

        return `rgba(${r},${g},${b},${opacity})`

    } else {
        console.error(colorCode, 'invalid color format: must be a valid hex or rgb string')
        return ''
    }
}


export const durations = [
    { label: 'Min', value: 'minutes' },
    { label: 'Hour', value: 'hours' }
]

/**
 * Convert duration in specific format
 * 
 * @param {number} duration 
 * @param {string} unit - 'hour', 'hours', 'minute', 'minutes'
 * @param {boolean} shortForm - default true
 * @returns 
 */
export const showDurationUnit = (duration, unit, shortForm = true) => {
    if (unit == 'minutes' || unit == 'minute') {
        if (duration > 1) {
            return shortForm ? `${duration} mins` : `${duration} minutes`
        } else {
            return shortForm ? `${duration} min` : `${duration} minute`
        }
    }
    if (unit == 'hour' || unit == 'hours') {
        if (duration > 1) {
            return shortForm ? `${duration} hrs` : `${duration} hours`
        } else {
            return shortForm ? `${duration} hr` : `${duration} hour`
        }
    }
}

export const sortedDays = () => {
    let first_day = window?.prebook_admin?.settings.calendar.first_day
        || window?.prebook?.settings.calendar.first_day
        || 'Sunday'

    // days accroding to [new Date()] format
    let days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

    if (first_day == 'Sunday') {
        return days
    }

    let sorted_days = days.splice(days.indexOf(first_day))

    return sorted_days.concat(days)
}

export const timeZones = [
    { label: 'UTC-12:00', value: 'UTC-12:00' },
    { label: 'UTC-11:00', value: 'UTC-11:00' },
    { label: 'UTC-10:00', value: 'UTC-10:00' },
    { label: 'UTC-09:00', value: 'UTC-09:00' },
    { label: 'UTC-08:00', value: 'UTC-08:00' },
    { label: 'UTC-07:00', value: 'UTC-07:00' },
    { label: 'UTC-06:00', value: 'UTC-06:00' },
    { label: 'UTC-05:00', value: 'UTC-05:00' },
    { label: 'UTC-04:00', value: 'UTC-04:00' },
    { label: 'UTC-03:00', value: 'UTC-03:00' },
    { label: 'UTC-02:00', value: 'UTC-02:00' },
    { label: 'UTC-01:00', value: 'UTC-01:00' },
    { label: 'UTC+00:00', value: 'UTC+00:00' },
    { label: 'UTC+01:00', value: 'UTC+01:00' },
    { label: 'UTC+02:00', value: 'UTC+02:00' },
    { label: 'UTC+03:00', value: 'UTC+03:00' },
    { label: 'UTC+04:00', value: 'UTC+04:00' },
    { label: 'UTC+05:00', value: 'UTC+05:00' },
    { label: 'UTC+06:00', value: 'UTC+06:00' },
    { label: 'UTC+07:00', value: 'UTC+07:00' },
    { label: 'UTC+08:00', value: 'UTC+08:00' },
    { label: 'UTC+09:00', value: 'UTC+09:00' },
    { label: 'UTC+10:00', value: 'UTC+10:00' },
    { label: 'UTC+11:00', value: 'UTC+11:00' },
    { label: 'UTC+12:00', value: 'UTC+12:00' },
]


export const isInteger = (number) => {
    return /^\d+$/.test(number)
}

export const validationCheck = (conditions) => {
    let errors = 0
    conditions.forEach((item) => {
        if (item.condition) {
            errors++
            Toast.warning(item.message)
        }
    })
    return {
        errors: errors > 0 ? true : false
    }
}

export const regex = {
    email: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    hexCode: /^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/,
    phone: /^(?:\+)?(?:[0-9\s-.()\[\]]){6,20}$/
}

export const addSpaceAfterComma = (text) => {
    return text.replace(/,(\S)/g, ', $1')
}

export const getHashQueryParam = (param) => {
    let queryString = window.location.hash.split('?')[1]
    return new URLSearchParams(queryString).get(param)
}

export const removeHashQueryParam = (param) => {
    let [hash, queryString] = window.location.hash.split('?')
    let params = new URLSearchParams(queryString)
    params.delete(param)
    window.location.hash = params.toString() ? `${hash}?${params}` : hash
};

